import classNames from 'classnames';
import React, { CSSProperties, useCallback, useEffect } from 'react';
import _ from 'underscore';
import { useTable, usePagination } from 'react-table';
import { useDebounce } from 'use-debounce';
import plural from 'helpers/plural';
import useEffectSkipInitial from 'helpers/use_effect_skip_initial';
import Button from './button';
import classnames from 'classnames';
import { useAngularService } from 'helpers/get_angular_service';
import Pagination from './pagination';
import TableSize from './table_size';
import Icon from './icon';

interface CustomRowProps {
  [key: string]: string;
}

interface Sorting {
  [x: string]: string;
}

interface CustomCellProps {
  hidden?: boolean;
  colSpan?: number;
  style?: CSSProperties;
}

interface TableProps {
  columns: any;
  data: any;
  loading?: boolean;
  emptyText?: any;
  pageSize: number;
  setPageSize?: (val: number) => void;
  loadData: (page: number) => void;
  filters?: any;
  sorting?: null | Sorting;
  toggleSorting?: (key: string, val: string) => void;
  withSearchBar?: boolean;
  variant?: 'v2';
  mobileNoWrap?: boolean;
  headerHidden?: boolean;
  getRowProps?: (row: any) => CustomRowProps;
  getCellProps?: (cell: any) => CustomCellProps;
  selectLabel?: string;
  selectedCount?: number;
  allPagesSelected?: boolean;
  toggleAllPages?: () => void;
  toggleAllPagesVisible?: boolean;
  className?: string;
  noBorderTop?: boolean;
  ignoreSortingChange?: boolean;
  syncQueryParams?: boolean;
  footerLabel?: any;
  footerLabelValue?: any;
  pageSizeColSpan?: number;
  showEmptyIcon?: boolean;
}

const defaultFilters = {};

const Table = ({
  loadData,
  filters = defaultFilters,
  columns,
  data,
  loading,
  emptyText,
  pageSize,
  sorting,
  setPageSize,
  withSearchBar,
  variant,
  headerHidden,
  getRowProps,
  getCellProps,
  selectLabel,
  selectedCount,
  allPagesSelected,
  toggleAllPages,
  toggleAllPagesVisible,
  mobileNoWrap,
  toggleSorting,
  className,
  noBorderTop,
  ignoreSortingChange,
  syncQueryParams,
  footerLabel,
  footerLabelValue,
  pageSizeColSpan,
  showEmptyIcon,
}: TableProps) => {
  const getRowId = useCallback((row, relativeIndex, parent) => {
    const key = row.id ? [row.id, relativeIndex].join('.') : relativeIndex;
    return parent ? [parent.id, key].join('.') : key;
  }, []);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize },
      manualPagination: true,
      getRowId,
    },
    usePagination,
  );
  const [debouncedFilters] = useDebounce(filters, 300);
  const [debouncedSorting] = useDebounce(sorting, 300, {
    equalityFn: _.isEqual,
  });

  const $location = useAngularService('$location');

  useEffect(() => {
    loadData(1);
    if (syncQueryParams) {
      $location.search(debouncedFilters);
    }
  }, [debouncedFilters]);

  useEffectSkipInitial(() => {
    loadData(1);
  }, [pageSize]);

  useEffectSkipInitial(() => {
    if (ignoreSortingChange) {
      return;
    }
    loadData(1);
  }, [debouncedSorting]);

  useEffect(() => {
    if (data && !data.length && data.meta && data.meta.page > 1) {
      loadData(data.meta.page - 1);
    }
  }, [data]);

  let pages = 0;

  if (data.meta) {
    pages = Math.ceil(data.meta.total / data.meta.count);
  }

  return (
    <div className="wide-table-scroller">
      <table
        className={classNames({
          'table worx-table': !className,
          [className]: !!className,
          'worx-table--with-header-addon': withSearchBar,
          'worx-table--v2': variant === 'v2',
          'worx-table--no-border-top': noBorderTop || (withSearchBar && headerHidden),
          'worx-table--mobile-nowrap': mobileNoWrap,
        })}
        {...getTableProps()}
      >
        {headerHidden ? null : (
          <thead>
            {toggleAllPagesVisible ? (
              <tr>
                <th colSpan={100} className="text-center">
                  {allPagesSelected ? (
                    <span>
                      All matching {selectLabel} selected.{' '}
                      <Button variant="link" onClick={() => toggleAllPages()}>
                        Click here to clear selection.
                      </Button>
                    </span>
                  ) : (
                    <span>
                      {plural(selectLabel, selectedCount, true)} selected on this page.{' '}
                      <Button variant="link" onClick={() => toggleAllPages()}>
                        Click here to select all pages.
                      </Button>
                    </span>
                  )}
                </th>
              </tr>
            ) : null}

            {headerGroups.map((headerGroup) => (
              // eslint-disable-next-line react/jsx-key
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  // eslint-disable-next-line react/jsx-key
                  <th {...column.getHeaderProps()} className={classNames(column.headerClass)}>
                    {column.sortable ? (
                      <Button
                        variant="clear"
                        onClick={() => {
                          const newVal = sorting && sorting[column.id] === 'asc' ? 'desc' : 'asc';

                          toggleSorting(column.id, newVal);
                        }}
                      >
                        <span
                          className={classnames('sortable-column', {
                            'sortable-column--active': sorting && sorting[column.id],
                          })}
                        >
                          {column.render('Header')}
                          {!column.hideSortingArrow && (
                            <i
                              className={classnames('margin-left-8', {
                                'icon-chevron-down':
                                  !sorting || !sorting[column.id] || sorting[column.id] === 'desc',
                                'icon-chevron-up': sorting && sorting[column.id] === 'asc',
                              })}
                            ></i>
                          )}
                        </span>
                      </Button>
                    ) : (
                      column.render('Header')
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        )}
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            const rowProps = getRowProps ? getRowProps(row) : {};

            return (
              // eslint-disable-next-line react/jsx-key
              <tr {...row.getRowProps()} {...rowProps}>
                {row.cells.map((cell) => {
                  const cellProps = getCellProps ? getCellProps(cell) : {};

                  return cellProps.hidden ? null : (
                    <td {...cell.getCellProps()} {...cellProps}>
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {!page.length && (
            <tr>
              <td colSpan={100} className={showEmptyIcon && !loading ? 'text-center' : ''}>
                {loading ? (
                  'Loading...'
                ) : showEmptyIcon ? (
                  <div className="empty-state margin-top-24 margin-bottom-24">
                    <Icon name="empty-table" width={110} height={80} />
                    <div className="helper-text">{emptyText || 'No results'}</div>
                  </div>
                ) : (
                  emptyText || 'No results'
                )}
              </td>
            </tr>
          )}
        </tbody>
        {(setPageSize || (!!data.meta && pages > 1)) && (
          <tfoot>
            <tr>
              {footerLabel ? (
                <>
                  <td colSpan={pageSizeColSpan}>
                    <TableSize pageSize={pageSize} setPageSize={setPageSize} />
                  </td>
                  <td>{footerLabel}</td>
                  <td>
                    <strong>{footerLabelValue}</strong>
                  </td>

                  <td colSpan={100}>
                    <Pagination data={data} setPage={(page) => loadData(page)} variant={variant} />
                  </td>
                </>
              ) : (
                <td colSpan={100}>
                  <TableSize pageSize={pageSize} setPageSize={setPageSize} />

                  {footerLabel}

                  <Pagination data={data} setPage={(page) => loadData(page)} variant={variant} />
                </td>
              )}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default Table;
